import {useCallback} from "react";
import {companyKeys} from "@/hooks/queries/queryKeys";
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store";
import {useQuery} from "@tanstack/react-query";
import {useShallow} from "zustand/react/shallow";
import {companyAPI} from "@/app/apiPaths";
import {GetCompanyDetailsResponse} from "@/pb/api/devplan/services/web/company";

export const useCompany = (id: number) => {
  return useOptionalCompany(id)
}

export function useCompanySettings(id: number | undefined) {
  const {fetchCompanySettings} = useBoundStore()
  const queryKey = companyKeys.settings(id ?? -1)
  const queryFn = useCallback(async () => {
    if (id === undefined) {
      return true
    }
    await fetchCompanySettings(id)
    return true
  }, [fetchCompanySettings, id])
  const enabled = id !== undefined
  const {error, refetch: reload, isPending} = useQuery({queryKey, queryFn, enabled})
  const settings = useBoundStore(useShallow(s => id !== undefined && id in s.companySettings ? s.companySettings[id] : undefined))
  return {settings, error, loading: enabled && isPending, reload}
}

export const useOptionalCompany = (id: number | undefined) => {
  const {setCompany, removeCompany} = useBoundStore()
  const queryKey = companyKeys.detail(id ?? -1)
  const queryFn = useCallback(async () => {
    if (!id) {
      return ""
    }
    const resp = await fetch(companyAPI(id))
    if (!resp.ok) {
      return Promise.reject(resp.statusText)
    }
    const {details} = GetCompanyDetailsResponse.fromJSON(await resp.json())
    if (details) {
      setCompany(details)
    } else {
      removeCompany(id)
    }
    return id
  }, [id, removeCompany, setCompany])
  const {error, refetch: reload, isPending: loading} = useQuery({queryKey, queryFn})
  const company = useBoundStore(useShallow(s => id !== undefined && id in s.companies ? s.companies[id] : undefined))
  return {company, error, loading, reload};
}
